import PropTypes from "prop-types";
import React, { useState, useRef, useEffect } from "react";
import { Box, TextField, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { enterKeyCode, resourceObjectTypes } from "../../constants";
import { sortBy } from "lodash";

const useStyles = makeStyles(theme => ({
  fillBlankText: {
    verticalAlign: "sub",
    marginRight: theme.spacing(1)
  },
  fillInTheBlankAnswer: {
    verticalAlign: "middle"
  },
  fillInTheBlankAnswerTextField: {
    width: 280
  },
  fillInTheBlankAnswerInput: {
    textOverflow: "ellipsis",
    paddingRight: theme.spacing(0.75)
  },
  fillInTheBlankText: {
    lineHeight: "58px"
  }
}));

const FillInTheBlankAnswer = ({ onSaveAnswer, existingAnswer, disabled }) => {
  const classes = useStyles();
  const [value, setValue] = useState("");
  const inputRef = useRef(null);

  const handleSaveAnswer = () => {
    onSaveAnswer(value);
    // This will cause the input to lose focus when the enter key is pressed
    inputRef.current.blur();
  };

  useEffect(() => {
    setValue(existingAnswer);
  }, [existingAnswer]);

  return (
    <Box mr={1} className={classes.fillInTheBlankAnswer} display="inline-flex">
      <TextField
        data-qa="fill-blank-question-answer-field"
        disabled={disabled}
        value={value || ""}
        variant="filled"
        className={classes.fillInTheBlankAnswerTextField}
        InputProps={{
          onBlur: () => handleSaveAnswer(),
          onKeyDown: event =>
            event.keyCode === enterKeyCode && handleSaveAnswer(), // saves when enter is pressed
          inputProps: {
            ref: inputRef,
            className: classes.fillInTheBlankAnswerInput
          }
        }}
        onChange={e => setValue(e.target.value)}
        hiddenLabel
      />
    </Box>
  );
};

const FillInTheBlankText = ({ text }) => {
  const classes = useStyles();

  return (
    <Box component="span" className={classes.fillInTheBlankText}>
      <Box component="span" className={classes.fillBlankText}>
        <Typography
          variant="body1"
          display="inline"
          data-qa="fill-blank-question-text"
        >
          {text}
        </Typography>
      </Box>
    </Box>
  );
};

const FillInTheBlankQuestion = ({
  fillInTheBlanks,
  onChange,
  existingAnswers,
  disabled
}) => {
  const [fillInTheBlankList, setFillInTheBlankList] = useState([]);

  useEffect(() => {
    setFillInTheBlankList(sortBy(fillInTheBlanks, "position"));
  }, [fillInTheBlanks]);

  const handleSaveAnswer = answerBlank => newUserAnswerText => {
    // TODO: Fix snake case for answer text
    onChange({ answer_text: newUserAnswerText, blankId: answerBlank.id });
  };

  const getExisingAnswer = answerBlank => {
    const existingAnswer = existingAnswers
      ? existingAnswers.find(
          answer => `${answer.blankId}` === `${answerBlank.id}`
        )
      : null;
    return existingAnswer ? existingAnswer.answerText : "";
  };

  return (
    <Box display="flex" alignItems="flex-start" mt={3.5} ml={3.75}>
      <Box component="span" lineHeight={5.5} flex="1">
        {fillInTheBlankList.map((fillInTheBlank, index) =>
          fillInTheBlank.type === resourceObjectTypes.fillInTheBlankTextType ? (
            <FillInTheBlankText
              key={`text_${fillInTheBlank.id}`}
              text={fillInTheBlank.text}
            />
          ) : (
            <FillInTheBlankAnswer
              key={`answer_${fillInTheBlank.id}`}
              existingAnswer={getExisingAnswer(fillInTheBlank)}
              onSaveAnswer={handleSaveAnswer(fillInTheBlank)}
              disabled={disabled}
            />
          )
        )}
      </Box>
    </Box>
  );
};

FillInTheBlankQuestion.propTypes = {
  fillInTheBlanks: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf([
        resourceObjectTypes.fillInTheBlankAnswerType,
        resourceObjectTypes.fillInTheBlankTextType
      ]),
      position: PropTypes.number,
      answerNum: PropTypes.number,
      text: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  onChange: PropTypes.func,
  existingAnswers: PropTypes.arrayOf(
    PropTypes.shape({
      answerData: PropTypes.object,
      /**
       * Used to map to the ID for the fillInTheBlankAnswer
       */
      blankId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // blankId and questionId might be numbers based on what is returned from the backend but ideally we always want IDs to be strings
      questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      /** The blank's current user input */
      answerText: PropTypes.string
    })
  ),
  /**
   * Used to disable the blanks when the quiz has already been submitted
   */
  disabled: PropTypes.bool
};

export default FillInTheBlankQuestion;
